import { omit } from 'lodash';
import type React from 'react';

import { FeatureFlag } from '@revelio/core';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';
import {
  COMPOSITION_GET_DATA_V2,
  COMPOSITION_GET_DATA_V3,
  CompensationDimension,
  type CompositionV2DataQuery,
  type CompositionV2Filters,
  type CompositionV3DataQuery,
  GET_TOP_N,
} from '@revelio/data-access';

import { useCustomSkillTaxonomyTree } from '../../../../../shared/selection-lists/hooks/useCustomSkillTaxonomyTree';
import { useSelectionListState } from '../../../../../shared/selection-lists/hooks/useSelectionListState';
import { useFilterContext } from '../../../filters/contexts/FilterProvider';
import { useEffectiveFilters } from '../../../filters/hooks/useEffectiveFilters';
import { useGeographyMode } from '../../../filters/hooks/useGeographyMode';
import { useRoleSelectionLists } from '../../../filters/selectionList/useRoleSelectionList';
import {
  FilterId,
  FilterType,
  type PrimaryFilterId,
} from '../../../filters/types/filters';
import {
  type CompositionV3BreakdownDimension,
  getCompositionV3BreakdownOptions,
} from '../../../filters/utils/composition-v3/getCompositionV3BreakdownOptions';
import {
  getCompositionV3Variables,
  getCustomSkillDeepestLevelFromFilters,
  normalizeCompositionEffectiveFilters,
} from '../../../filters/utils/composition-v3/getCompositionV3Variables';
import { mergeDrilldownIntoCompositionV3Filters } from '../../../filters/utils/composition-v3/merge-drilldown-into-composition-v3-filters';
import { getMonthRefsFromCompositionV3Filters } from '../../../filters/utils/composition-v3/month-refs-from-v3-filters';
import {
  mapCompositionV2EntitiesToV3,
  stripEntitiesFromFilters,
} from '../../../filters/utils/composition-v3/role-skill-ent-kind';
import { getCustomSkillLevelId } from '../../../filters/utils/customSkillTaxonomyRegistry';
import { getTopNFilters } from '../../../filters/utils/get-top-n-filters';
import { getCompositionV2Filters } from '../../../filters/utils/getCompositionV2Filters';
import { getDim1 } from '../../../filters/utils/getDim1';
import { TOP_N_LIMIT } from '../constants';
import { getTopEntities } from '../TopNDataProvider';
import type { DataProviderQueryStatus } from '../types';
import {
  combineQueryStatus,
  useUrqlQueryWithStatus,
} from '../utils/data-provider-results-utils';
import { adaptCompositionV3ToLegacyCompositionV2ForTreemap } from './composition-v3/adapt-composition-v3-to-legacy-treemap';
import { isOtherCompositionBucket } from './composition-v3/is-empty-composition-bucket';

export type DrilldownFilter =
  | {
      segment: 'geography';
      level: 'region' | 'country' | 'msa' | 'city';
      id: number;
    }
  | {
      segment: 'role';
      levelId: 'role_k7' | 'role_k150' | 'role_k1500' | 'role_k15000';
      id: string;
    }
  | {
      segment: 'rics';
      level: 'rics_k10' | 'rics_k50' | 'rics_k400' | 'company';
      id: number;
    }
  | {
      segment: 'skills';
      level: 'skill_0' | 'skill_1' | 'skill_2' | 'skill_3' | 'skill_4';
      id: string;
    };

const SEGMENT_TO_BREAKDOWN_KEY: Record<
  DrilldownFilter['segment'],
  'Geography' | 'Role' | 'Rics' | 'Skill'
> = {
  geography: 'Geography',
  role: 'Role',
  rics: 'Rics',
  skills: 'Skill',
};

const SEGMENT_TO_V3_BREAKDOWN_DIMENSION: Record<
  DrilldownFilter['segment'],
  CompositionV3BreakdownDimension
> = {
  geography: 'geography',
  role: 'role',
  rics: 'rics',
  skills: 'skill',
};

function mergeDrilldownIntoFilters(
  base: CompositionV2Filters,
  drilldown: DrilldownFilter
): CompositionV2Filters {
  switch (drilldown.segment) {
    case 'geography':
      return {
        ...base,
        region: drilldown.level === 'region' ? [drilldown.id] : undefined,
        country: drilldown.level === 'country' ? [drilldown.id] : undefined,
        msa: drilldown.level === 'msa' ? [drilldown.id] : undefined,
        city: drilldown.level === 'city' ? [drilldown.id] : undefined,
      };
    case 'role':
      return {
        ...base,
        custom_role: {
          ...(base.custom_role ?? {}),
          entities: [{ id: drilldown.id, levelId: drilldown.levelId }],
        },
      };
    case 'rics':
      return {
        ...base,
        rics_k10: drilldown.level === 'rics_k10' ? [drilldown.id] : undefined,
        rics_k50: drilldown.level === 'rics_k50' ? [drilldown.id] : undefined,
        rics_k400:
          drilldown.level === 'rics_k400' || drilldown.level === 'company'
            ? [drilldown.id]
            : undefined,
      };
    case 'skills': {
      const entityId = parseInt(drilldown.id.split('_').slice(-1)[0] ?? '', 10);
      const levelId = getCustomSkillLevelId(drilldown.level);
      if (!Number.isFinite(entityId) || levelId === undefined) {
        return base;
      }
      return {
        ...base,
        custom_skill: {
          taxonomy_id: base.custom_skill?.taxonomy_id ?? 0,
          from_new_refs: true,
          entities: [{ entity_id: entityId, level_id: levelId }],
        },
      };
    }
  }
}

function filterOutSkillsOtherBucket(
  data: CompositionV2DataQuery | undefined,
  drilldown: DrilldownFilter
): CompositionV2DataQuery | undefined {
  if (data === undefined || drilldown.segment !== 'skills') return data;
  const composition = data.composition_v2;
  if (composition === undefined || composition === null) return data;
  return {
    ...data,
    composition_v2: {
      ...composition,
      entities: composition.entities.map((entity) => ({
        ...entity,
        metrics: {
          ...entity.metrics,
          skills: entity.metrics.skills.filter(
            (skill) => !isOtherCompositionBucket(skill.metadata.id)
          ),
        },
      })),
    },
  };
}

type Props = {
  primaryDimension: PrimaryFilterId;
  drilldown: DrilldownFilter;
  children: (result: {
    data: CompositionV2DataQuery | undefined;
    status: DataProviderQueryStatus;
  }) => React.ReactNode;
};

export function CompositionTreemapDrilldownProviderLegacy({
  primaryDimension,
  drilldown,
  children,
}: Props) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListState();
  const roleSelectionLists = useRoleSelectionLists();
  const { customSkillTree } = useCustomSkillTaxonomyTree();
  const hasCustomSkillSelections = effectiveFilters.some(
    (f) =>
      f.id === FilterId.CUSTOM_SKILL &&
      f.type === FilterType.TREE &&
      f.selections.length > 0
  );
  const isWaitingForSkillTaxonomy =
    hasCustomSkillSelections && customSkillTree === null;
  const geographyMode = useGeographyMode();

  const selectionListsWithRole = {
    ...selectionLists,
    ...roleSelectionLists,
  };

  const baseVariables = getCompositionV2Filters({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    selectionLists: selectionListsWithRole,
    geographyMode,
    ...(drilldown.segment === 'skills' && {
      customSkillTree,
      customSkillSelections: [drilldown.id],
      customSkillDeepestLevel: drilldown.level,
    }),
  });

  const mergedFilters = mergeDrilldownIntoFilters(
    baseVariables.filters,
    drilldown
  );

  const breakdownKey = SEGMENT_TO_BREAKDOWN_KEY[drilldown.segment];
  const drilldownVariables = {
    ...baseVariables,
    filters: mergedFilters,
    breakdownOptions: {
      ...baseVariables.breakdownOptions,
      [breakdownKey]: { all: true },
    },
  };

  const isRicsCompanyBreakdown =
    drilldown.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company');

  const topNFilters = isRicsCompanyBreakdown
    ? {
        ...getTopNFilters(effectiveFilters),
        rics_k400: [drilldown.id],
      }
    : null;

  const dim1 = getDim1(primaryDimension);
  const [{ data: topNData, status: topNStatus }] = useUrqlQueryWithStatus({
    query: GET_TOP_N,
    variables: {
      filters: topNFilters ?? {},
      dim1: dim1 ?? undefined,
      dim2: CompensationDimension.Company,
    },
    pause:
      !isRicsCompanyBreakdown ||
      dim1 === undefined ||
      isWaitingForSkillTaxonomy,
  });

  const topEntities =
    isRicsCompanyBreakdown && topNData?.compensation
      ? getTopEntities({
          data: topNData.compensation,
          secondaryDimension: 'company',
          filters: topNFilters ?? {},
        })?.slice(0, TOP_N_LIMIT)
      : undefined;

  const compositionVariables = (() => {
    if (
      isRicsCompanyBreakdown &&
      topEntities != null &&
      topEntities.length > 0
    ) {
      const filtersWithoutRics = omit(drilldownVariables.filters, [
        'rics_k10',
        'rics_k50',
        'rics_k400',
      ]);
      return {
        ...drilldownVariables,
        filters: filtersWithoutRics,
        entities: topEntities,
      };
    }
    return drilldownVariables;
  })();

  const compositionPause =
    isRicsCompanyBreakdown && (!topEntities || topEntities.length === 0);

  const [{ data, status: compositionStatus }] = useUrqlQueryWithStatus({
    query: COMPOSITION_GET_DATA_V2,
    variables: compositionVariables,
    pause: compositionPause,
  });

  const status = (() => {
    if (isRicsCompanyBreakdown) {
      return combineQueryStatus([topNStatus, compositionStatus]);
    }
    return compositionStatus;
  })();

  const finalData = (() => {
    if (
      isRicsCompanyBreakdown &&
      (compositionPause ||
        (topNData != null && (!topEntities || topEntities.length === 0)))
    ) {
      return undefined;
    }
    return filterOutSkillsOtherBucket(data, drilldown);
  })();

  return <>{children({ data: finalData, status })}</>;
}

export function CompositionTreemapDrilldownProviderV3({
  primaryDimension,
  drilldown,
  children,
}: Props) {
  const effectiveFilters = useEffectiveFilters({ viewGranularity: 'month' });
  const { selectionLists } = useSelectionListState();
  const roleSelectionLists = useRoleSelectionLists();
  const { customSkillTree } = useCustomSkillTaxonomyTree();
  const { activeFilterMap, geographyMode } = useFilterContext();

  const selectionListsWithRole = {
    ...selectionLists,
    ...roleSelectionLists,
  };

  const baseVariablesV21 = getCompositionV3Variables({
    primaryDimension,
    effectiveFilters,
    isSnapshot: true,
    getSelectionBreakdowns: true,
    selectionLists: selectionListsWithRole,
    geographyMode,
    customSkillTree,
    activeFilterMap,
  });

  const mergedFiltersV21 = mergeDrilldownIntoCompositionV3Filters(
    baseVariablesV21.filters,
    drilldown
  );

  const normalizedEffectiveForBreakdown =
    normalizeCompositionEffectiveFilters(effectiveFilters);

  const breakdownCustomSkillDeepestLevel =
    drilldown.segment === 'skills'
      ? drilldown.level
      : getCustomSkillDeepestLevelFromFilters(normalizedEffectiveForBreakdown);

  const breakdownOptionsV21 = getCompositionV3BreakdownOptions({
    compositionFilters: mergedFiltersV21,
    customSkillTree,
    customSkillDeepestLevel: breakdownCustomSkillDeepestLevel,
    includedBreakdownDimensions: [
      SEGMENT_TO_V3_BREAKDOWN_DIMENSION[drilldown.segment],
    ],
    geographyMode,
  });

  const drilldownVariablesV21 = {
    ...baseVariablesV21,
    filters: mergedFiltersV21,
    breakdown_options: breakdownOptionsV21,
  };

  const isRicsCompanyBreakdown =
    drilldown.segment === 'rics' &&
    (drilldown.level === 'rics_k400' || drilldown.level === 'company');

  const topNFilters = isRicsCompanyBreakdown
    ? {
        ...getTopNFilters(effectiveFilters),
        rics_k400: [drilldown.id],
      }
    : null;

  const dim1 = getDim1(primaryDimension);
  const [{ data: topNData, status: topNStatus }] = useUrqlQueryWithStatus({
    query: GET_TOP_N,
    variables: {
      filters: topNFilters ?? {},
      dim1: dim1 ?? undefined,
      dim2: CompensationDimension.Company,
    },
    pause: !isRicsCompanyBreakdown || dim1 === undefined,
  });

  const topEntities =
    isRicsCompanyBreakdown && topNData?.compensation
      ? getTopEntities({
          data: topNData.compensation,
          secondaryDimension: 'company',
          filters: topNFilters ?? {},
        })?.slice(0, TOP_N_LIMIT)
      : undefined;

  const compositionVariablesV21 = (() => {
    if (
      isRicsCompanyBreakdown &&
      topEntities != null &&
      topEntities.length > 0
    ) {
      const filtersWithoutRics = omit(mergedFiltersV21, [
        'rics_k10',
        'rics_k50',
        'rics_k400',
      ]);
      const entities = mapCompositionV2EntitiesToV3(topEntities) ?? [];
      return {
        ...drilldownVariablesV21,
        filters: stripEntitiesFromFilters(filtersWithoutRics, entities),
        entities,
      };
    }
    return drilldownVariablesV21;
  })();

  const compositionPause =
    isRicsCompanyBreakdown && (!topEntities || topEntities.length === 0);

  const [{ data: dataV21, status: compositionStatusV21 }] =
    useUrqlQueryWithStatus<CompositionV3DataQuery>({
      query: COMPOSITION_GET_DATA_V3,
      variables: compositionVariablesV21,
      pause: compositionPause,
    });

  const status = (() => {
    if (isRicsCompanyBreakdown) {
      return combineQueryStatus([topNStatus, compositionStatusV21]);
    }
    return compositionStatusV21;
  })();

  const requestEntityKind = (() => {
    const ents = compositionVariablesV21.entities;
    const first = Array.isArray(ents) ? ents[0] : ents;
    return first?.EntType;
  })();

  const finalData = (() => {
    if (
      isRicsCompanyBreakdown &&
      (compositionPause ||
        (topNData != null && (!topEntities || topEntities.length === 0)))
    ) {
      return undefined;
    }
    const v21 = dataV21?.composition_v3;
    if (!v21) {
      return undefined;
    }
    const legacyShaped = {
      composition_v2: adaptCompositionV3ToLegacyCompositionV2ForTreemap(
        v21,
        getMonthRefsFromCompositionV3Filters(compositionVariablesV21.filters),
        requestEntityKind
      ),
    } as CompositionV2DataQuery;
    return filterOutSkillsOtherBucket(legacyShaped, drilldown);
  })();

  return <>{children({ data: finalData, status })}</>;
}

export function CompositionTreemapDrilldownProvider(props: Props) {
  const isTreemapCompositionV3 = useUnleashFlag(FeatureFlag.CompositionV3);
  if (isTreemapCompositionV3) {
    return <CompositionTreemapDrilldownProviderV3 {...props} />;
  }
  return <CompositionTreemapDrilldownProviderLegacy {...props} />;
}
